import { useEffect, useState } from 'react'

import { Box, Button, HStack, IconButton, Popover, Portal, Spacer, Text, useDisclosure } from '@chakra-ui/react'
import { ResetIcon } from '@editor/assets/icons'
import { useBlueprintEditorContext } from '@editor/pages/projects/blueprint-editor/contexts/BlueprintEditorContext'
import Chrome, { ChromeInputType } from '@uiw/react-color-chrome'

interface ColorSettingProps {
  title: string
  color?: string
  defaultColor?: string
  visible?: boolean
  onChange: (color: string) => void
  onUpdate: (color?: string) => void
}

const ColorSetting = ({ title, color, defaultColor, visible, onChange, onUpdate }: ColorSettingProps) => {
  // Contexts
  const { shouldPreventExitEditingMode, shouldTogglePreventExitEditingMode } = useBlueprintEditorContext()

  // States
  const [tempColor, setTempColor] = useState(color)

  /**
   * Update local values when current styles change.
   */
  useEffect(() => {
    setTempColor(color)
  }, [color])

  /**
   * Manual control of stroke popover due to bug with the popover not closing
   * when changing the stroke width while the popover is open.
   * No need for fill color since the popover covers the stroke width input.
   */
  const { open, setOpen } = useDisclosure()

  const handleOpenChange = (state: { open: boolean }) => {
    setOpen(state.open)
    if (!state.open) {
      onUpdate(tempColor)
      shouldTogglePreventExitEditingMode.current = true
    }
  }

  return (
    <HStack gap={0} w="100%" display={visible ? 'flex' : 'none'}>
      <Text mr={9}>{title}</Text>
      <Spacer />
      <Popover.Root open={open} onOpenChange={handleOpenChange} positioning={{ strategy: 'fixed' }}>
        <Popover.Trigger asChild>
          <Box position="relative" overflow="hidden" borderRadius="md">
            {!color || color === 'transparent' ? (
              <Box
                borderLeft="3px solid red"
                position="absolute"
                top="-2px"
                left={0}
                zIndex={1}
                width="100%"
                height="130%"
                transform="rotate(52deg) translateX(45%)"
              />
            ) : null}
            <Button
              variant="outline"
              maxWidth={6}
              minWidth={6}
              width={6}
              height={5}
              p={0}
              backgroundColor={tempColor}
              borderColor="secondary.700"
              _hover={{ bg: tempColor }}
              _focus={{ bg: tempColor }}
              _active={{ bg: tempColor }}
              onMouseDown={() => {
                shouldPreventExitEditingMode.current = true
              }}
            />
          </Box>
        </Popover.Trigger>

        <Portal>
          <Popover.Positioner>
            <Popover.Content w="auto" borderWidth={0}>
              <Popover.Arrow />
              <Popover.Body p={0} overflow="hidden" bg="none" borderRadius="10px">
                <Chrome
                  inputType={ChromeInputType.HEXA}
                  showAlpha={false}
                  showEyeDropper={false}
                  color={tempColor}
                  onChange={(clr) => {
                    setTempColor(clr.hex)
                    onChange(clr.hex)
                  }}
                />
              </Popover.Body>
            </Popover.Content>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
      <IconButton
        aria-label="reset"
        fontSize="sm"
        onClick={(e) => {
          e.stopPropagation()
          onUpdate(defaultColor)
        }}
        size="xs"
        variant="toolbarIcon"
      >
        <ResetIcon />
      </IconButton>
    </HStack>
  )
}

export default ColorSetting
